import { Alert, Box, Button, IconButton, Popper, Snackbar, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCalculatedDateRange } from '../../../hooks/useCalculatedDateRange.ts';
import { useProcessedRaces } from '../../../hooks/useProcessedRaces.ts';
import { useCaseApi } from '../../../services/api/useCase.api.ts';
import { useDisparityStageApi } from '../../../services/api/useDisparityStage.api.ts';
import { useDisparityTypeApi } from '../../../services/api/useDisparityType.api.ts';
import { Case } from '../../../types/Case/Case.type.ts';
import { NewDisparity } from '../../../types/Disparity/NewDisparity.type.ts';
import { DisparitySubType } from '../../../types/enums/DisparitySubType.enum.ts';
import { createEnhancementDyadsFromCase, EnhancementDyad } from '../../../utils/enhancementDyads.ts';
import ComparatorTool from '../../ComparatorTool/ComparatorTool.tsx';
import DisparityFieldsForm, { DisparityFieldsData } from '../DisparityFieldsForm.tsx';

interface NewDisparitySearchProps {
  open: boolean;
  anchorEl?: HTMLElement | null;
  close: () => void;
  caseData?: Case;
  onDisparitySelect?: (disparityId: number) => void;
}

function NewDisparitySearch({ open, anchorEl, close, caseData, onDisparitySelect }: NewDisparitySearchProps) {
  const queryClient = useQueryClient();
  const { getDisparityStages } = useDisparityStageApi();
  const { getDisparityTypes } = useDisparityTypeApi();
  const { postDisparityForCase } = useCaseApi();

  const [disparityFields, setDisparityFields] = useState<DisparityFieldsData>({
    statuteIds: [],
    stageId: -1,
    typeId: -1,
    subtypeId: null,
    raceId: null,
    startDate: null,
    endDate: null,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedEnhancementDyad, setSelectedEnhancementDyad] = useState<EnhancementDyad | null>(null);

  const enhancementDyads = useMemo<EnhancementDyad[]>(() => createEnhancementDyadsFromCase(caseData), [caseData]);

  const handleDisparityError = (error: unknown) => {
    let message = 'Failed to create disparity search. Please try again.';

    if (error instanceof Error && error.message) {
      message = error.message;
    }

    setErrorMessage(message);
    setSnackbarOpen(true);
  };

  const { processedRaces } = useProcessedRaces({ caseData, includeComparisonRaces: true });
  const { startDate, endDate, minDate, maxDate, resetDates } = useCalculatedDateRange({
    caseData,
  });

  const { data: disparityStages } = useQuery({
    queryKey: ['disparityStages'],
    queryFn: () => getDisparityStages(),
  });

  const { data: disparityTypes } = useQuery({
    queryKey: ['disparityTypes'],
    queryFn: () => getDisparityTypes(),
  });

  const { mutate: createDisparity, isPending: isCreatingDisparity } = useMutation({
    mutationKey: ['postDisparityForCase'],
    mutationFn: ({ disparityData }: { disparityData: NewDisparity }) =>
      postDisparityForCase(Number(caseData?.id), disparityData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });

      if (data?.id) {
        onDisparitySelect?.(data.id);
      }

      setDisparityFields({
        statuteIds: caseData?.statutes ? [caseData.statutes[0].id] : [],
        stageId: -1,
        typeId: disparityTypes?.[0]?.id ?? -1,
        subtypeId: null,
        raceId: processedRaces?.[0]?.id ?? null,
        startDate: null,
        endDate: null,
      });

      resetDates();
      close();
    },
    onError: (error) => {
      handleDisparityError(error);
    },
  });

  const [prevOpen, setPrevOpen] = useState(open);
  if (open && !prevOpen) {
    setDisparityFields({
      statuteIds: caseData?.statutes ? [caseData.statutes[0].id] : [],
      stageId: -1,
      typeId: disparityTypes?.[0]?.id ?? -1,
      subtypeId: null,
      raceId: processedRaces?.[0]?.id ?? null,
      startDate: startDate,
      endDate: endDate,
    });
    setSelectedEnhancementDyad(null);
    resetDates();
  }
  if (open !== prevOpen) {
    setPrevOpen(open);
  }

  // the user's choice, else the first race once races have loaded
  const fields = { ...disparityFields, raceId: disparityFields.raceId ?? processedRaces?.[0]?.id ?? null };

  const selectedType = disparityTypes?.find((type) => type.id === disparityFields.typeId);
  const isWobblerSelected = disparityFields.subtypeId === DisparitySubType.WOBBLERS;

  const canSearch =
    disparityFields.statuteIds.length > 0 &&
    disparityFields.stageId !== null &&
    disparityFields.typeId !== null &&
    fields.raceId !== null &&
    disparityFields.startDate !== null &&
    disparityFields.endDate !== null &&
    (!selectedType?.subtypes?.length || disparityFields.subtypeId !== null);

  const handleDisparityFieldChange = (field: keyof DisparityFieldsData, value: Date | number | number[] | null) => {
    setDisparityFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchButtonClick = () => {
    if (!disparityFields.startDate || !disparityFields.endDate) return;

    const disparityData: NewDisparity = {
      statutes_ids: disparityFields.statuteIds,
      stage_id: disparityFields.stageId!,
      type_id: disparityFields.typeId!,
      ...(selectedType?.subtypes?.length &&
        disparityFields.subtypeId !== null && { subtype_id: disparityFields.subtypeId }),
      race_id: fields.raceId!,
      start_date: format(disparityFields.startDate, 'yyyy-MM-dd'),
      end_date: format(disparityFields.endDate, 'yyyy-MM-dd'),
    };
    createDisparity({ disparityData });
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        disablePortal={true}
        sx={{
          padding: '16px 28px',
          bgcolor: 'background.paper',
          zIndex: 1000,
          borderRadius: '8px',
          boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '480px',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
              altBoundary: true,
              tether: true,
              altAxis: true,
              rootBoundary: 'viewport',
            },
          },
        ]}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingX: 0,
            marginBottom: '20px',
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            Search Details
          </Typography>
          <IconButton onClick={close}>
            <XIcon size={24} />
          </IconButton>
        </Box>
        <Box sx={{ paddingX: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <DisparityFieldsForm
            caseData={caseData}
            disparityStages={disparityStages}
            disparityTypes={disparityTypes}
            processedRaces={processedRaces}
            values={fields}
            onChange={handleDisparityFieldChange}
            minDate={minDate}
            maxDate={maxDate}
            singleStatuteOnly={isWobblerSelected}
            enhancementDyads={enhancementDyads}
            selectedEnhancementDyad={selectedEnhancementDyad}
            onEnhancementDyadChange={setSelectedEnhancementDyad}
          />
        </Box>
        <Box
          sx={{
            paddingX: 0,
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '32px',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ComparatorTool
              forNewDisparity
              countyId={caseData?.county.id}
              caseData={caseData}
              disparityStages={disparityStages}
              disparityTypes={disparityTypes}
              processedRaces={processedRaces}
              initialDisparityFields={fields}
              initialEnhancementDyad={selectedEnhancementDyad}
              onError={handleDisparityError}
              onSuccess={(disparity) => {
                onDisparitySelect?.(disparity.id);
                close();
              }}
            />
          </Box>
          <Button
            disableElevation
            variant='contained'
            color='accentBlue'
            sx={{ color: '#fff', paddingX: '48px', fontWeight: 600 }}
            onClick={handleSearchButtonClick}
            disabled={!canSearch || isCreatingDisparity}
          >
            Search
          </Button>
        </Box>
      </Popper>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity='error' sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default NewDisparitySearch;
